/* Overhead Allocation Simulator — Phase 1 (display + simulation only).

   Surfaces the G&A pool (52xxx) and shows how it WOULD be allocated to
   active projects under different bases. No journal is posted.

   Route:  /overhead-report
   Access: Admin / Accountant / CFO
*/
import React, { useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Info, Save, ShieldCheck, FileText, RotateCcw } from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-US',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = new Date();
const defaultPeriod = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

const BASES = [
  { key: 'revenue',     label: '% of Revenue',     hint: 'Higher revenue ⇒ higher overhead burden' },
  { key: 'direct_cost', label: '% of Direct Cost', hint: 'Higher direct cost ⇒ higher overhead burden' },
  { key: 'equal',       label: 'Equal Split',      hint: 'Pool ÷ N projects equally' },
  { key: 'fixed',       label: 'Fixed per Project', hint: 'Same SAR amount on each active project' },
];

export default function OverheadReport() {
  const [period, setPeriod] = useState(defaultPeriod);
  const [basis, setBasis]   = useState('revenue');
  const [fixedRate, setFixedRate] = useState('');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period, basis });
      if (basis === 'fixed' && fixedRate) params.append('fixed_rate', fixedRate);
      const r = await axiosInstance.get(`/overhead/report?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [period, basis, fixedRate]);

  // ───────── Phase 2 — Scenarios + Posting ─────────
  const [scenarios, setScenarios] = useState([]);
  const [scnLoading, setScnLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadScenarios = useCallback(async () => {
    setScnLoading(true);
    try {
      const r = await axiosInstance.get('/overhead/scenarios');
      setScenarios(r.data || []);
    } catch (e) {
      toast.error(`Load scenarios failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setScnLoading(false);
    }
  }, []);

  const saveScenario = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ period, basis });
      if (basis === 'fixed' && fixedRate) params.append('fixed_rate', fixedRate);
      params.append('label', `${period} · ${basis}`);
      await axiosInstance.post(`/overhead/scenarios?${params.toString()}`);
      toast.success(`📌 Scenario saved (draft) for ${period} · ${basis}`);
      await loadScenarios();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const approve = async (sid) => {
    if (!window.confirm('Approve this scenario? It will become eligible for posting (no journal yet).')) return;
    setBusy(true);
    try {
      await axiosInstance.post(`/overhead/scenarios/${sid}/approve`);
      toast.success('✓ Approved');
      await loadScenarios();
    } catch (e) {
      toast.error(`Approve failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const postJournal = async (sid) => {
    // Patch 23 — Allocation Safety Gate preview. Surface every block
    // BEFORE asking the operator for confirmation so they don't sign
    // off on something the server will refuse anyway.
    try {
      const gate = await axiosInstance.get(`/overhead/scenarios/${sid}/safety-gate`);
      if (gate.data && gate.data.ok === false) {
        const reasons = (gate.data.blocks || []).map(b => `• ${b.reason}`).join('\n');
        toast.error('Posting blocked by safety gate:\n' + reasons, { duration: 10_000 });
        return;
      }
    } catch (e) {
      // If the preview itself errors, fall through to the post attempt;
      // the server-side gate will still enforce the policy.
      console.warn('Safety-gate preview failed (continuing):', e?.message);
    }
    const c1 = window.confirm('⚠️ POST allocation journal? This creates real journal entries in the ledger.');
    if (!c1) return;
    const c2 = window.prompt('Type POST to confirm:');
    if (c2 !== 'POST') { toast.info('Cancelled.'); return; }
    setBusy(true);
    try {
      const r = await axiosInstance.post(`/overhead/scenarios/${sid}/post?confirm=true`);
      toast.success(`✅ Journal posted: ${r.data?.journal?.journal_no}`);
      await loadScenarios();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object' && d.error === 'allocation_safety_gate_blocked') {
        const reasons = (d.blocks || []).map(b => `• ${b.reason}`).join('\n');
        toast.error('Posting blocked by safety gate:\n' + reasons, { duration: 10_000 });
      } else {
        toast.error(`Post failed: ${typeof d === 'string' ? d : (d?.message || e.message)}`);
      }
    } finally { setBusy(false); }
  };

  const reverse = async (sid) => {
    const reason = window.prompt('Reason for reversal? (Required)');
    if (!reason) { toast.info('Cancelled.'); return; }
    setBusy(true);
    try {
      const r = await axiosInstance.post(
        `/overhead/scenarios/${sid}/reverse?reason=${encodeURIComponent(reason)}&confirm=true`);
      toast.success(`↩ Reversed via ${r.data?.reversal_journal?.journal_no}`);
      await loadScenarios();
    } catch (e) {
      toast.error(`Reverse failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const reject = async (sid) => {
    const reason = window.prompt('Why are you rejecting this scenario? (Required, becomes permanent audit record)');
    if (!reason) { toast.info('Cancelled.'); return; }
    setBusy(true);
    try {
      await axiosInstance.post(
        `/overhead/scenarios/${sid}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success(`🚫 Rejected — recorded in audit trail`);
      await loadScenarios();
    } catch (e) {
      toast.error(`Reject failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="overhead-report-page">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-slate-900">📊 Overhead Allocation Simulator</h1>
        <p className="text-sm text-slate-600">
          محاكاة توزيع المصاريف الإدارية (52xxx) على المشاريع — Phase 1 (عرض فقط، لا قيود)
        </p>
      </header>

      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Period (YYYY-MM)</label>
          <input
            data-testid="overhead-period-input"
            type="text"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            placeholder="2026-06"
            className="border border-slate-300 rounded px-3 py-1.5 text-sm font-mono w-32"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Allocation Basis</label>
          <select
            data-testid="overhead-basis-select"
            value={basis}
            onChange={e => setBasis(e.target.value)}
            className="border border-slate-300 rounded px-3 py-1.5 text-sm w-48"
          >
            {BASES.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
          <p className="text-[10px] text-slate-500 mt-0.5">{BASES.find(b=>b.key===basis)?.hint}</p>
        </div>
        {basis === 'fixed' && (
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Fixed Rate (SAR per project)</label>
            <input
              data-testid="overhead-fixed-rate-input"
              type="number"
              value={fixedRate}
              onChange={e => setFixedRate(e.target.value)}
              placeholder="e.g., 5000"
              className="border border-slate-300 rounded px-3 py-1.5 text-sm w-40"
            />
          </div>
        )}
        <button
          data-testid="overhead-run-btn"
          onClick={run}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Simulate
        </button>
        {data && (
          <button
            data-testid="overhead-save-scenario-btn"
            onClick={saveScenario}
            disabled={busy}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white rounded px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
          >
            <Save size={14} /> Save as Scenario (draft)
          </button>
        )}
        <button
          data-testid="overhead-load-scenarios-btn"
          onClick={loadScenarios}
          disabled={scnLoading}
          className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 rounded px-3 py-1.5 text-sm disabled:opacity-50"
        >
          {scnLoading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
          Load Scenarios
        </button>
      </div>

      {/* Results */}
      {data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {[
              { label: 'G&A Pool (52xxx)',         value: data.pool?.total,                          accent: 'bg-amber-50  text-amber-900  border-amber-200'  },
              { label: 'Active Projects',           value: data.totals?.active_projects, isInt: true, accent: 'bg-sky-50    text-sky-900    border-sky-200'    },
              { label: 'Total Allocated',           value: data.totals?.total_allocated_overhead,    accent: 'bg-emerald-50 text-emerald-900 border-emerald-200'},
              { label: 'Total Profit (after OH)',   value: data.totals?.total_profit_after_overhead, accent: 'bg-indigo-50  text-indigo-900  border-indigo-200' },
            ].map((c, i) => (
              <div key={i} data-testid={`overhead-kpi-${i}`} className={`rounded-lg p-3 border ${c.accent}`}>
                <div className="text-[11px] uppercase tracking-wider opacity-80">{c.label}</div>
                <div className="text-xl font-bold mt-1 font-mono">
                  {c.isInt ? (c.value ?? 0) : `${fmt(c.value)} SAR`}
                </div>
              </div>
            ))}
          </div>

          {/* Notes */}
          {data.notes?.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 space-y-1">
              <div className="flex items-center gap-1 font-semibold"><Info size={12} /> Notes</div>
              {data.notes.map((n, i) => <div key={i}>• {n}</div>)}
            </div>
          )}

          {/* Pool breakdown */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h2 className="text-sm font-bold text-slate-900 mb-2">📁 G&A Pool — Breakdown by Account</h2>
            <table className="w-full text-xs font-mono border-collapse" data-testid="overhead-pool-table">
              <thead>
                <tr className="bg-amber-50">
                  <th className="border px-2 py-1 text-left">Account #</th>
                  <th className="border px-2 py-1 text-left">Account Name</th>
                  <th className="border px-2 py-1 text-right">Amount (SAR)</th>
                </tr>
              </thead>
              <tbody>
                {(data.pool?.by_account || []).map((r, k) => (
                  <tr key={k}>
                    <td className="border px-2 py-1">{r.account_number}</td>
                    <td className="border px-2 py-1">{r.account_name}</td>
                    <td className="border px-2 py-1 text-right">{fmt(r.amount)}</td>
                  </tr>
                ))}
                <tr className="bg-amber-100 font-bold">
                  <td className="border px-2 py-1" colSpan={2}>Total</td>
                  <td className="border px-2 py-1 text-right">{fmt(data.pool?.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Project-level allocation */}
          <div className="bg-white border border-slate-200 rounded-lg p-4 overflow-x-auto">
            <h2 className="text-sm font-bold text-slate-900 mb-2">
              🏗 Per-Project Allocation Simulation ({data.basis})
            </h2>
            <table className="w-full text-xs font-mono border-collapse" data-testid="overhead-projects-table">
              <thead>
                <tr className="bg-slate-50">
                  <th className="border px-2 py-1 text-left">Project</th>
                  <th className="border px-2 py-1 text-right">Revenue</th>
                  <th className="border px-2 py-1 text-right">Direct Cost</th>
                  <th className="border px-2 py-1 text-right">Profit (now)</th>
                  <th className="border px-2 py-1 text-right">Margin %</th>
                  <th className="border px-2 py-1 text-right">Weight</th>
                  <th className="border px-2 py-1 text-right bg-rose-50">Allocated OH</th>
                  <th className="border px-2 py-1 text-right bg-emerald-50">Profit (after OH)</th>
                  <th className="border px-2 py-1 text-right">Margin (after)</th>
                </tr>
              </thead>
              <tbody>
                {(data.projects || []).map((p, k) => (
                  <tr key={k} className={p.profit_after_overhead < 0 ? 'bg-rose-50' : ''}>
                    <td className="border px-2 py-1">{p.project_name || p.project_code || p.project_id?.slice(0,8)}</td>
                    <td className="border px-2 py-1 text-right">{fmt(p.revenue)}</td>
                    <td className="border px-2 py-1 text-right">{fmt(p.direct_cost)}</td>
                    <td className="border px-2 py-1 text-right">{fmt(p.current_profit)}</td>
                    <td className="border px-2 py-1 text-right">{p.current_margin_pct?.toFixed(1)}%</td>
                    <td className="border px-2 py-1 text-right text-slate-500">
                      {p.weight != null ? (p.weight * 100).toFixed(2) + '%' : '—'}
                    </td>
                    <td className="border px-2 py-1 text-right bg-rose-50 text-rose-800">{fmt(p.allocated_overhead)}</td>
                    <td className={`border px-2 py-1 text-right bg-emerald-50 font-semibold
                      ${p.profit_after_overhead < 0 ? 'text-rose-700' : 'text-emerald-800'}`}>
                      {fmt(p.profit_after_overhead)}
                    </td>
                    <td className="border px-2 py-1 text-right">{p.margin_after_overhead_pct?.toFixed(1)}%</td>
                  </tr>
                ))}
                <tr className="bg-slate-100 font-bold">
                  <td className="border px-2 py-1">Totals</td>
                  <td className="border px-2 py-1 text-right">{fmt(data.totals?.total_revenue)}</td>
                  <td className="border px-2 py-1 text-right">{fmt(data.totals?.total_direct_cost)}</td>
                  <td className="border px-2 py-1 text-right">{fmt(data.totals?.total_current_profit)}</td>
                  <td className="border px-2 py-1 text-right" />
                  <td className="border px-2 py-1 text-right" />
                  <td className="border px-2 py-1 text-right bg-rose-100 text-rose-800">{fmt(data.totals?.total_allocated_overhead)}</td>
                  <td className="border px-2 py-1 text-right bg-emerald-100 text-emerald-800">{fmt(data.totals?.total_profit_after_overhead)}</td>
                  <td className="border px-2 py-1 text-right" />
                </tr>
                {data.totals?.unallocated_pool != null && Math.abs(data.totals.unallocated_pool) > 0.01 && (
                  <tr className="bg-amber-50 text-amber-900 italic">
                    <td className="border px-2 py-1" colSpan={6}>Unallocated remainder (pool − Σ allocated):</td>
                    <td className="border px-2 py-1 text-right">{fmt(data.totals.unallocated_pool)}</td>
                    <td className="border px-2 py-1" colSpan={2} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!data && !loading && (
        <div className="text-center text-slate-500 text-sm py-12 border border-dashed border-slate-300 rounded-lg">
          Pick a period & basis, then click <strong>Simulate</strong>.
        </div>
      )}

      {/* ───────── Phase 2 — Scenarios List ───────── */}
      {scenarios.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 mt-6" data-testid="scenarios-section">
          <h2 className="text-sm font-bold text-slate-900 mb-2">
            📋 Saved Scenarios ({scenarios.length})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="border px-2 py-1 text-left">Label</th>
                  <th className="border px-2 py-1 text-left">Period</th>
                  <th className="border px-2 py-1 text-left">Basis</th>
                  <th className="border px-2 py-1 text-right">Pool (SAR)</th>
                  <th className="border px-2 py-1 text-right">Allocated (SAR)</th>
                  <th className="border px-2 py-1 text-left">Status</th>
                  <th className="border px-2 py-1 text-left">Journal</th>
                  <th className="border px-2 py-1 text-left">Created</th>
                  <th className="border px-2 py-1 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {scenarios.map((s) => {
                  const statusColors = {
                    draft:    'bg-slate-100 text-slate-800',
                    approved: 'bg-sky-100   text-sky-800',
                    posted:   'bg-emerald-100 text-emerald-800',
                    reversed: 'bg-rose-100  text-rose-800',
                    rejected: 'bg-zinc-200  text-zinc-700 line-through',
                  };
                  return (
                    <tr key={s.id} data-testid={`scenario-row-${s.id}`}>
                      <td className="border px-2 py-1">{s.label}</td>
                      <td className="border px-2 py-1">{s.period}</td>
                      <td className="border px-2 py-1">{s.basis}</td>
                      <td className="border px-2 py-1 text-right">{fmt(s.report?.pool?.total)}</td>
                      <td className="border px-2 py-1 text-right">{fmt(s.report?.totals?.total_allocated_overhead)}</td>
                      <td className="border px-2 py-1">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${statusColors[s.status] || 'bg-slate-100'}`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="border px-2 py-1 text-slate-600">
                        {s.journal_no || '—'}
                        {s.reversal_journal_no && <div className="text-rose-600">↩ {s.reversal_journal_no}</div>}
                      </td>
                      <td className="border px-2 py-1 text-slate-500">
                        {s.created_at?.slice(0, 16).replace('T', ' ')}<br />
                        <span className="text-[10px]">{s.created_by_name}</span>
                      </td>
                      <td className="border px-2 py-1 space-y-1">
                        {s.status === 'draft' && (
                          <>
                          <button
                            data-testid={`approve-btn-${s.id}`}
                            onClick={() => approve(s.id)}
                            disabled={busy}
                            className="px-2 py-0.5 bg-sky-600 hover:bg-sky-700 text-white rounded text-[10px] font-semibold inline-flex items-center gap-1"
                          ><ShieldCheck size={10} /> Approve</button>
                          <button
                            data-testid={`reject-btn-${s.id}`}
                            onClick={() => reject(s.id)}
                            disabled={busy}
                            className="px-2 py-0.5 ml-1 bg-zinc-500 hover:bg-zinc-600 text-white rounded text-[10px] font-semibold inline-flex items-center gap-1"
                          >🚫 Reject</button>
                          </>
                        )}
                        {s.status === 'approved' && (
                          <>
                          <button
                            data-testid={`post-btn-${s.id}`}
                            onClick={() => postJournal(s.id)}
                            disabled={busy}
                            className="px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-semibold inline-flex items-center gap-1"
                          >🔓 Post Journal</button>
                          <button
                            data-testid={`reject-after-approve-btn-${s.id}`}
                            onClick={() => reject(s.id)}
                            disabled={busy}
                            className="px-2 py-0.5 ml-1 bg-zinc-500 hover:bg-zinc-600 text-white rounded text-[10px] font-semibold"
                          >🚫 Reject</button>
                          </>
                        )}
                        {s.status === 'posted' && (
                          <button
                            data-testid={`reverse-btn-${s.id}`}
                            onClick={() => reverse(s.id)}
                            disabled={busy}
                            className="px-2 py-0.5 bg-rose-600 hover:bg-rose-700 text-white rounded text-[10px] font-semibold inline-flex items-center gap-1"
                          ><RotateCcw size={10} /> Reverse</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
